/**
 * LogoMark — the Holdem Drills brand crosshair (matches the marketing
 * site, holdemdrills.com): an amber ring that draws itself in on mount,
 * four cardinal ticks that fade in, and a center dot that pulses.
 *
 * Animation + reduced-motion handling live in `index.css` (the `.hd-mark`
 * classes) — this component is just the SVG geometry + class wiring, in
 * keeping with how the rest of the app hand-rolls custom CSS there
 * rather than per-component <style> tags.
 *
 * Animates ONCE per mount (fine for the persistent app shell / sign-in
 * splash — the shell doesn't remount on in-app navigation). The
 * non-animated state (animate={false} / reduced-motion) renders a
 * correct, fully-drawn static mark. Decorative (aria-hidden) — pair it
 * with a visible "Holdem Drills" wordmark or an aria-label on the link.
 *
 * Geometry is fixed in a 36-unit viewBox; scale via `size` only. If the
 * ring radius ever changes, recompute the stroke-dasharray in index.css
 * to ceil(2*pi*r).
 */

/** Brand amber — mirrors `--color-brand` in index.css / the marketing site. */
const BRAND_AMBER = "#e5a93c";

interface LogoMarkProps {
  /** Rendered width/height in px (square). Default 26. */
  size?: number;
  /** Stroke/fill color. Defaults to the brand amber. */
  color?: string;
  /** Play the draw-in + pulse animation. Default true. */
  animate?: boolean;
}

export function LogoMark({
  size = 26,
  color = BRAND_AMBER,
  animate = true,
}: LogoMarkProps) {
  return (
    <svg
      className={animate ? "hd-mark hd-mark--animate" : "hd-mark"}
      width={size}
      height={size}
      viewBox="0 0 36 36"
      fill="none"
      aria-hidden
    >
      <circle
        className="hd-ring"
        cx="18"
        cy="18"
        r="15"
        stroke={color}
        strokeWidth="1.6"
      />
      <line className="hd-tick" x1="18" y1="3.5" x2="18" y2="7.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
      <line className="hd-tick" x1="32.5" y1="18" x2="28.5" y2="18" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
      <line className="hd-tick" x1="18" y1="32.5" x2="18" y2="28.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
      <line className="hd-tick" x1="3.5" y1="18" x2="7.5" y2="18" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
      <circle className="hd-dot" cx="18" cy="18" r="3.4" fill={color} />
    </svg>
  );
}
